import { useState } from 'react'
import { ApiError, requestCode, verifyCode } from '../lib/api.js'

/** Two steps: request a code to marketing@pebro.de, then type it back. */
export function Login({ onDone }: { onDone: () => void }): JSX.Element {
  const [sentTo, setSentTo] = useState<string>()
  const [code, setCode] = useState('')
  const [error, setError] = useState<string>()
  const [notice, setNotice] = useState<string>()
  const [busy, setBusy] = useState(false)

  const failure = (err: unknown): string => (err instanceof ApiError ? err.message : 'Keine Verbindung zum Server.')

  const send = async (): Promise<void> => {
    setBusy(true)
    setError(undefined)
    setNotice(undefined)
    try {
      const result = await requestCode()
      setSentTo(result.sentTo)
      setCode('')
      if (result.cooldown) setNotice('Gerade erst verschickt. Bitte kurz warten und ins Postfach schauen.')
    } catch (err) {
      setError(failure(err))
    } finally {
      setBusy(false)
    }
  }

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault()
    setBusy(true)
    setError(undefined)
    try {
      await verifyCode(code)
      onDone()
    } catch (err) {
      setError(failure(err))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="safe-y flex min-h-full items-center justify-center bg-grouped px-6">
      <div className="w-full max-w-sm space-y-5">
        <div className="space-y-3 text-center">
          <img src="/icon-192.png" alt="" className="mx-auto h-20 w-20 rounded-[22%]" />
          <h1 className="font-brand text-h2 font-light tracking-tight">Marketing Hub</h1>
          <p className="text-small text-label2/60">pebro · EDELBAND · visualaized</p>
        </div>

        {sentTo === undefined ? (
          <button type="button" className="btn-primary w-full" disabled={busy} onClick={() => void send()}>
            {busy ? 'Wird verschickt …' : 'Anmeldecode per E-Mail anfordern'}
          </button>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <p className="text-center text-small text-label2/60">
              Code an <span className="font-medium text-label">{sentTo}</span> geschickt. 10 Minuten gültig.
            </p>
            <input
              className="field num text-center text-h3 tracking-[0.4em]"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              autoComplete="one-time-code"
              autoFocus
              placeholder="000000"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
            />
            <button type="submit" className="btn-primary w-full" disabled={busy || code.length !== 6}>
              {busy ? 'Prüfe …' : 'Anmelden'}
            </button>
            <button type="button" className="btn-quiet w-full text-small" disabled={busy} onClick={() => void send()}>
              Neuen Code anfordern
            </button>
          </form>
        )}

        {notice && <p className="text-center text-small text-label2/60">{notice}</p>}
        {error && <p className="text-center text-small text-danger">{error}</p>}
      </div>
    </div>
  )
}
